import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { TFunction } from 'i18next';

import DownloadResume from '@/assets/fulfill/inventory/DownloadResume';
import DownloadSKU from '@/assets/fulfill/inventory/DownloadSKU';
import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import { LotBreakdownRowState, Order } from '@/types/fulfill/inventoryHistory';
import { Countries, formatCurrency } from '@/utils/common';

import LotBreakdownPanel from './LotBreakdownPanel';
import { mapLotBreakdown } from './mapLotBreakdown';
import { getStatusStyle } from './utils';

const TOTAL_COLUMN_COUNT = 8;
const DATES_LABEL_COLUMN_WIDTH = '4.75rem';

const formatShortDate = (value: string | Date) => {
  const [day, month, year] = format(new Date(value), 'dd MMM yyyy', { locale: es }).split(' ');
  return `${day} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
};

interface InventoryHistoryProps {
  t: TFunction<'commonGeneral', undefined>;
  downloadLabels: (arrivalOrderNumber: string) => void;
  downloadSkuLabels: (legacyId: string) => void;
  downloadResume: (arrivalOrderNumber: string) => void;
  onShowPickups: (order: Order) => void;
  expandedRows: Record<string, LotBreakdownRowState>;
  toggleLotBreakdown: (order: Order) => void;
  finalizeLotCollapse: (order: Order) => void;
  country?: Countries;
}

interface Product {
  price: number;
  quantity: number;
  [key: string]: unknown;
}

export const inventoryColumns = ({
  t,
  downloadLabels,
  downloadSkuLabels,
  downloadResume,
  onShowPickups,
  expandedRows,
  toggleLotBreakdown,
  finalizeLotCollapse,
  country,
}: InventoryHistoryProps): Array<GridColDef> => [
  {
    field: 'orderId',
    flex: 1.2,
    headerName: `Nº de orden`,
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    colSpan: (_value, row) => (row.isDetailRow ? TOTAL_COLUMN_COUNT : undefined),
    renderCell: (params) => {
      if (params.row.isDetailRow) {
        const order = params.row.order as Order;
        return (
          <LotBreakdownPanel
            id={`lot-breakdown-panel-${order.aoNumber}`}
            open={params.row.expandState === 'open'}
            details={mapLotBreakdown(order)}
            onRequestClose={() => toggleLotBreakdown(order)}
            onExited={() => finalizeLotCollapse(order)}
          />
        );
      }
      return (
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.grey[600],
            mt: '1.9rem',
          }}
        >
          {params.row.aoNumber}
        </Typography>
      );
    },
  },
  {
    field: 'dates',
    flex: 1.15,
    headerName: 'Eventos',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      if (params.row.isDetailRow) return null;
      const order = params.row as Order;
      const dateRows: Array<[string, string]> = [
        ['Creación', formatShortDate(order.createdAt)],
        ['Llegada', formatShortDate(order.appointment)],
        ['Actualizado', formatShortDate(order.updatedAt)],
      ];
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          height="100%"
          rowGap={0.15}
        >
          {dateRows.map(([label, value]) => (
            <Box key={label} display="flex" gap={0.5}>
              <Typography
                variant="caption"
                sx={{
                  minWidth: DATES_LABEL_COLUMN_WIDTH,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', color: (theme) => theme.palette.grey[600] }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    },
  },
  {
    field: 'parcels',
    flex: 0.7,
    headerName: 'Bultos',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      const parcelsCount = params.row.parcels ? Number(params.row.parcels) : 0;
      const pickupRequired = params.row.pickupRequired === true;
      const hasPickups = params.row.pickups?.hasPickups === true;
      const hasFailed = params.row.pickups?.hasFailed === true;
      const totalCreated = params.row.pickups?.totalCreated || 0;
      const taskTracking = params.row.pickups?.taskTracking;
      const isProcessing = !!taskTracking && taskTracking.status === 'processing';

      let iconColor = 'rgba(0, 0, 0, 0.06)';
      let tooltipTitle = 'Sin recolecciones programadas';
      let isClickable = false;

      if (pickupRequired) {
        if (isProcessing) {
          iconColor = '#ff9800';
          tooltipTitle = `Procesando recolecciones… (${taskTracking.completed}/${taskTracking.total})`;
          isClickable = false;
        } else if (hasPickups && !hasFailed) {
          iconColor = '#85c441';
          tooltipTitle = 'Ver recolecciones';
          isClickable = true;
        } else if (hasFailed) {
          iconColor = '#ef5350';
          tooltipTitle = hasPickups
            ? 'Error: Algunas recolecciones fallaron'
            : 'Error: Falló al crear recolección';
          isClickable = true;
        } else {
          iconColor = '#ef5350';
          tooltipTitle = 'Error: Sin recolecciones creadas';
          isClickable = true;
        }
      }

      const highlightYellow = pickupRequired && isProcessing;
      const highlightRed =
        pickupRequired && !isProcessing && (parcelsCount !== totalCreated || hasFailed);

      return (
        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontWeight: 'bold',
              color: highlightYellow
                ? '#ff9800'
                : highlightRed
                  ? (theme) => theme.palette.error.main
                  : (theme) => theme.palette.grey[600],
            }}
          >
            {parcelsCount}
          </Typography>
          <Tooltip followCursor title={tooltipTitle} placement="top">
            <span>
              <IconButton
                size="small"
                color="inherit"
                disabled={!isClickable}
                onClick={isClickable ? () => onShowPickups(params.row) : undefined}
                sx={{
                  padding: 0.5,
                  '& .MuiSvgIcon-root': {
                    color: iconColor,
                  },
                }}
              >
                <LocalShippingIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    },
  },
  {
    field: 'received',
    flex: 1,
    renderHeader: () => (
      <Typography
        variant="body2"
        align="center"
        sx={{
          fontWeight: 'bold',
          lineHeight: 1.2,
          whiteSpace: 'pre-line',
        }}
      >
        Recibido/{'\n'}Ordenado
      </Typography>
    ),
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      if (params.row.isDetailRow) return null;
      const order = params.row as Order;
      const hasLots = mapLotBreakdown(order).length > 0;
      const isExpanded = expandedRows[params.row.aoNumber] === 'open';
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Typography
            variant="body2"
            display={'inline'}
            sx={{ fontWeight: 'bold', color: (theme) => theme.palette.grey[600] }}
          >
            {params.row.total_qty_received}/{params.row.total_qty}
          </Typography>
          {hasLots && (
            <ButtonBase
              className="lot-breakdown-toggle"
              onClick={() => toggleLotBreakdown(params.row)}
              aria-expanded={isExpanded}
              aria-controls={`lot-breakdown-panel-${params.row.aoNumber}`}
              aria-label={isExpanded ? 'Cerrar lotes de la orden' : 'Ver lotes de la orden'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                mt: 0.25,
                px: 0.5,
                borderRadius: 1,
                color: (theme) => theme.palette.primary.main,
                '&:focus-visible': {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <Typography variant="caption" fontWeight="bold">
                {isExpanded ? 'Cerrar' : 'Ver lotes'}
              </Typography>
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: '1rem' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
              )}
            </ButtonBase>
          )}
        </Box>
      );
    },
  },
  {
    field: 'cost',
    flex: 0.8,
    headerName: 'Valor',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    renderCell: (params) => {
      if (params.row.isDetailRow) return null;
      const products = Array.isArray(params.row.products) ? params.row.products : [];
      const total = products.reduce((p: number, c: Product) => {
        const price = Number(c?.price) || 0;
        const quantity = Number(c?.quantity) || 0;
        return p + price * quantity;
      }, 0);
      return (
        <Typography
          variant="body2"
          display={'inline'}
          sx={{ fontWeight: 'bold', color: (theme) => theme.palette.grey[600] }}
        >
          {formatCurrency(total, country)}
        </Typography>
      );
    },
  },
  {
    field: 'inventoryStatus',
    flex: 1,
    headerName: 'Recepción',
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const config = getStatusStyle(params.row.inventoryStatus, 'inventory');
      return (
        <StatusChip
          status={t(`inventoryStatus.${params.row.inventoryStatus}`)}
          color={config.color}
          bg={config.bg}
        />
      );
    },
  },
  {
    field: 'orderStatus',
    flex: 1,
    headerName: 'Estatus',
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const config = getStatusStyle(params.row.status, 'order');
      return (
        <Box>
          <StatusChip
            status={t(`status.${params.row.status}`)}
            color={config.color}
            bg={config.bg}
          />
        </Box>
      );
    },
  },
  {
    field: 'actions',
    flex: 1,
    minWidth: 150,
    headerName: 'Acciones',
    align: 'center',
    sortable: false,
    headerAlign: 'center',
    resizable: false,
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 140,
            mt: 2.5,
          }}
        >
          <Tooltip followCursor title="Etiquetas" placement="top">
            <IconButton color="primary" onClick={() => downloadLabels(params.row.aoNumber)}>
              <CloudDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip followCursor title="SKU" placement="top">
            <IconButton color="primary" onClick={() => downloadSkuLabels(params.row.legacy_id)}>
              <DownloadSKU />
            </IconButton>
          </Tooltip>
          <Tooltip followCursor title="Resumen" placement="top">
            <IconButton color="primary" onClick={() => downloadResume(params.row.aoNumber)}>
              <DownloadResume />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  },
];
